import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { CardBody, CardHeader } from "reactstrap";
import Form from "./Form";
import NTable from "../../../components/NTable";
import Drawer from "../../../components/Drawer";
import Button from "../../../components/Button";
import { useDeleteModal } from "../../Settings/helpers/useDeleteModal";
import { useNewTable } from "../../../components/NTable/hook";
import { useInitialState } from "../../../utils/hooks/useInitialState";
import { useDrawerProps } from "../../Settings/helpers/useDrawerProps";
import { useColumns } from "./columns";
import { ProfileContext } from "../context";
import { useExpectedHoursApi } from "./api";
import { useAccess } from "../../../utils/hooks/access";

const useCustomDrawer = (getChildren) => {
	const [wrapperElement, setWrapperElement] = useState(null);

	useLayoutEffect(() => {
		const element = document.getElementById("expected-hours-drawer");
		setWrapperElement(element);
	}, [setWrapperElement]);

	if (wrapperElement === null) return null;

	return createPortal(getChildren(), wrapperElement);
};

function ExpectedHours({ isOpenFromTimesheet }) {
	const { t } = useTranslation();
	const { user, isSelf } = useContext(ProfileContext);
	const base = isSelf ? "profile.expectedHours" : "people.expectedHours";
	const { hasAccess: _canCreate } = useAccess(`${base}.canCreate`);
	const canCreate = isOpenFromTimesheet ? false : _canCreate;
	const { hasAccess: _canEdit } = useAccess(`${base}.canEdit`);
	const canEdit = isOpenFromTimesheet ? false : _canEdit;
	const { hasAccess: _canDelete } = useAccess(`${base}.canDelete`);
	const canDelete = isOpenFromTimesheet ? false : _canDelete;

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		create,
		update,
		goToCreate,
		goToEdit,
		goToView,
		remove,
	} = useExpectedHoursApi();

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: () => t("do-you-want-to-delete-this-expected-hours-allocation"),
		onConfirm: (id) => remove(id),
		permission: isSelf ? "profile.expectedHours.canDelete" : "people.expectedHours.canDelete",
	});
	const openDeleteModalWithAccess = canDelete ? openDeleteModal : undefined;

	const columns = useColumns({
		edit: canEdit ? goToEdit : undefined,
		remove: openDeleteModalWithAccess,
	});

	const table = useNewTable({
		data,
		totalItems,
		loading,
		columns,
		enableRowSelection: false,
		initialState: useInitialState(() => ({
			sorting: [{ id: "startEffectiveDate", desc: true }],
		})),
	});

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		create: t("assign-expected-hours"),
		edit: t("edit-expected-hours"),
	});

	const drawer = useCustomDrawer(() => (
		<Drawer
			title={drawerTitle}
			open={drawerOpen}
			close={goToView}
			style={{ maxWidth: "initial" }}
		>
			<Form
				mode={mode}
				error={error}
				loading={submitting}
				close={goToView}
				create={create}
				update={update}
				values={selected}
			/>
		</Drawer>
	));

	useEffect(() => {
		if (!user?.id) return;
		const controller = new AbortController();
		fetch(controller);
		return () => controller.abort();
	}, [fetch, user?.id]);

	return (
		<>
			<CardHeader className="border-0">
				<div className="d-flex justify-content-between">
					<div className="d-flex flex-column">
						<h3 className="mb-0">{t("expected-hours")}</h3>
						<p className="text-muted mb-0">{t("expected-hours-historical-view")}</p>
					</div>
					{canCreate && (
						<Button
							onClick={goToCreate}
							className="border-dark pr-3 btn-round btn-icon shadow-none btn btn-none btn-sm h-25"
						>
							{t("assign-expected-hours")}
						</Button>
					)}
				</div>
			</CardHeader>

			<CardBody
				className="p-0"
				style={{
					height: "calc(100% - 20px)",
					overflowY: "auto",
				}}
			>
				<NTable table={table} />
			</CardBody>

			{drawer}
			{deleteModal}
		</>
	);
}

export default ExpectedHours;
